import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';

const Testimonial: React.FC = () => {
  const { t, language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 'rajendra',
      name: t('testimonial.rajendra.name'),
      location: t('testimonial.rajendra.location'),
      quote: t('testimonial.rajendra.quote'),
      marathiQuote: t('testimonial.rajendra.marathi'),
      rating: 5
    },
    {
      id: 'mehul',
      name: t('testimonial.mehul.name'),
      location: t('testimonial.mehul.location'),
      quote: t('testimonial.mehul.quote'),
      rating: 5
    },
    {
      id: 'thandiwe',
      name: t('testimonial.thandiwe.name'),
      location: t('testimonial.thandiwe.location'),
      quote: t('testimonial.thandiwe.quote'),
      rating: 5
    },
    {
      id: 'khalid',
      name: t('testimonial.khalid.name'),
      location: t('testimonial.khalid.location'),
      quote: t('testimonial.khalid.quote'),
      rating: 5
    },
    {
      id: 'priyanka',
      name: t('testimonial.priyanka.name'),
      location: t('testimonial.priyanka.location'),
      quote: t('testimonial.priyanka.quote'),
      rating: 5
    }
  ];

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-24 bg-gradient-to-b from-emerald-50 to-white dark:from-emerald-950/20 dark:to-background-dark relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-emerald-900 dark:text-white">
            {t('testimonial.title')}
          </h2>
          <div className="flex justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-primary text-primary" />
            ))}
          </div>
          <p className="text-emerald-700/60 dark:text-emerald-300/40 mt-4 max-w-2xl mx-auto">
            Real stories from real people who transformed their gardens and farms with Samson Orgo
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          {/* Main Testimonial Card */}
          <div className="relative">
            {/* Navigation Buttons */}
            <button
              onClick={goToPrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-white dark:bg-emerald-900 text-emerald-900 dark:text-white p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-white dark:bg-emerald-900 text-emerald-900 dark:text-white p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Testimonial Card */}
            <div className="bg-white dark:bg-emerald-950/40 p-8 md:p-12 rounded-3xl shadow-2xl relative border border-emerald-100 dark:border-emerald-800/30">
              <Quote className="absolute top-8 left-8 w-16 h-16 text-primary/10" />
              
              <div className="relative z-10">
                {/* Rating */}
                <div className="flex gap-1 mb-6">
                  {[...Array(currentTestimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-primary text-primary" />
                  ))}
                </div>
                
                {/* Quote */}
                <div className="mb-8">
                  {language === 'mr' && currentTestimonial.id === 'rajendra' && (
                    <p className="text-lg md:text-xl font-medium text-primary mb-4 italic border-l-4 border-primary pl-4">
                      {currentTestimonial.marathiQuote}
                    </p>
                  )}
                  <p className="text-lg md:text-xl text-emerald-800 dark:text-emerald-100 leading-relaxed">
                    {currentTestimonial.quote}
                  </p>
                </div>
                
                {/* Author Info */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center text-white font-bold text-xl">
                    {currentTestimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h4 className="font-bold text-xl text-emerald-900 dark:text-white">
                      {currentTestimonial.name}
                    </h4>
                    <p className="text-emerald-600 dark:text-emerald-400">
                      {currentTestimonial.location}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-primary w-6' 
                    : 'bg-primary/20 hover:bg-primary/40'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
          
          {/* Testimonial Counter */}
          <p className="text-center text-emerald-600 dark:text-emerald-400 mt-4">
            {currentIndex + 1} / {testimonials.length}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Testimonial;